import {
  CreditLineDetailSchema,
  CreditLineSummarySchema,
  CreditMovementSchema,
  CustomerProfileSchema,
  DrawdownSchema,
  PaymentIntentSchema,
  StatementSchema,
  type PaymentIntent,
  type SagaStep,
} from '@paguz/contracts';
import { z } from 'zod';
import {
  AdminCustomerDetailSchema,
  AdminCustomerPageSchema,
  AdminLineDetailSchema,
  AdminPaymentIntentDetailSchema,
  AdminPaymentIntentPageSchema,
  EmittedEventSchema,
  OperatorActionResultSchema,
  type AdminPaymentIntentDetail,
  type AdminPaymentIntentSummary,
  type ExternalSystem,
} from './admin-schemas.ts';

/**
 * Alineación con la API real (`services/api` módulo `backoffice`): sus respuestas de
 * transacciones, clientes y líneas son más delgadas que las vistas de la consola. Cada esquema
 * acepta la vista completa (MSW / API futura) **o** la forma real actual y la normaliza. La API
 * entrega el cliente enmascarado (listado y detalle) y los eventos emitidos con su webhook; el
 * sistema de cada paso sale del paso de la saga y la duración de sus marcas de tiempo.
 */

const SYSTEM_BY_STEP: Readonly<Record<SagaStep, ExternalSystem>> = {
  VALIDATE: 'PAGUZ',
  LIMITS: 'PAGUZ',
  AUTHORIZE: 'CREDIT_CORE',
  FUND: 'DEPOSIT_CORE',
  SEND: 'BREB',
  AWAIT_CONFIRMATION: 'BREB',
  CAPTURE: 'CREDIT_CORE',
  DEBIT: 'DEPOSIT_CORE',
  RELEASE: 'CREDIT_CORE',
  REVERSE_FUNDING: 'DEPOSIT_CORE',
};
const SYSTEM_BY_PORT: Readonly<Record<string, ExternalSystem>> = {
  CreditCorePort: 'CREDIT_CORE',
  DepositCorePort: 'DEPOSIT_CORE',
  BreBPort: 'BREB',
  NotificationPort: 'NOTIFICATIONS',
};

const unknownCustomer = (id: string) => ({
  id,
  fullName: 'Cliente',
  maskedDocument: '—',
  maskedPhone: '—',
});

const RealCustomerSchema = z
  .object({
    id: z.string(),
    fullName: z.string(),
    maskedDocument: z.string(),
    maskedPhone: z.string(),
  })
  .nullable()
  .optional();
type RealCustomer = z.infer<typeof RealCustomerSchema>;

export function intentToSummary(
  intent: PaymentIntent,
  customer?: RealCustomer,
): AdminPaymentIntentSummary {
  return {
    id: intent.id,
    status: intent.status,
    customer: customer ?? unknownCustomer(intent.customerId),
    payeeName: intent.payee.name,
    payeeAlias: intent.payee.aliasValue,
    amount: intent.amount,
    installments: intent.installments,
    channel: intent.channel,
    fundingMode: intent.fundingMode,
    reasonCode: intent.reasonCode,
    endToEndId: intent.endToEndId,
    createdAt: intent.createdAt,
  };
}

export const PaymentIntentListResponseSchema = z.union([
  AdminPaymentIntentPageSchema,
  z
    .object({
      data: z.array(PaymentIntentSchema.and(z.object({ customer: RealCustomerSchema }))),
      nextCursor: z.string().nullable(),
    })
    .transform((page) => ({
      data: page.data.map((intent) => intentToSummary(intent, intent.customer)),
      nextCursor: page.nextCursor,
    })),
]);

const RealIntentDetailSchema = z.object({
  intent: PaymentIntentSchema,
  customer: RealCustomerSchema,
  /** Eventos emitidos (outbox) con su entrega webhook; ausente en versiones previas de la API. */
  events: z.array(EmittedEventSchema).optional(),
  technical: z.looseObject({
    traceId: z.string().nullable().optional(),
    creditAuthorizationRef: z.string().nullable().optional(),
    brebResult: z.unknown().optional(),
  }),
  externalCalls: z.array(
    z.looseObject({
      port: z.string(),
      adapter: z.string(),
      operation: z.string(),
      outcome: z.string(),
      latencyMs: z.number(),
      request: z.unknown(),
      response: z.unknown(),
      at: z.string(),
    }),
  ),
  exceptions: z.array(z.looseObject({ id: z.string(), type: z.string(), status: z.string() })),
});

export function realDetailToView(
  real: z.infer<typeof RealIntentDetailSchema>,
): AdminPaymentIntentDetail {
  const { intent, technical } = real;
  const breb = typeof technical.brebResult === 'string' ? technical.brebResult : null;
  const open = real.exceptions.find((e) => e.status === 'OPEN');
  return {
    intent,
    customer: real.customer ?? unknownCustomer(intent.customerId),
    authorizationId: technical.creditAuthorizationRef ?? null,
    traceId: technical.traceId ?? '',
    saga: (intent.steps ?? []).map((s, i) => ({
      id: `${s.step}-${String(s.attempt)}-${String(i)}`,
      step: s.step,
      status: s.status,
      attempt: s.attempt,
      system: SYSTEM_BY_STEP[s.step],
      adapter: '',
      startedAt: s.startedAt,
      finishedAt: s.finishedAt,
      durationMs: s.finishedAt
        ? Math.max(0, Date.parse(s.finishedAt) - Date.parse(s.startedAt))
        : null,
      externalCode: null,
      reasonCode: s.reasonCode,
      idempotent: true,
      compensation: s.step === 'RELEASE' || s.step === 'REVERSE_FUNDING',
      externalCallIds: [],
    })),
    externalCalls: real.externalCalls.map((c, i) => ({
      id: `call_${String(i + 1)}`,
      system: SYSTEM_BY_PORT[c.port] ?? 'PAGUZ',
      adapter: c.adapter,
      operation: `${c.port}.${c.operation}`,
      method: '',
      endpoint: c.operation,
      httpStatus: null,
      latencyMs: Math.round(c.latencyMs),
      startedAt: c.at,
      outcome:
        c.outcome === 'OK' || c.outcome === 'SUCCESS'
          ? 'OK'
          : c.outcome === 'TIMEOUT'
            ? 'TIMEOUT'
            : 'ERROR',
      request: c.request,
      response: c.response,
    })),
    events: real.events ?? [],
    breb: {
      status:
        breb === 'ACCEPTED' || breb === 'COMPLETED'
          ? 'COMPLETED'
          : breb === 'REJECTED'
            ? 'REJECTED'
            : breb
              ? 'PENDING'
              : 'UNKNOWN',
      checkedAt: null,
    },
    openException: open ? { id: open.id, type: open.type, createdAt: intent.createdAt } : null,
  };
}

export const PaymentIntentDetailResponseSchema = z.union([
  AdminPaymentIntentDetailSchema,
  RealIntentDetailSchema.transform(realDetailToView),
]);

/** Acciones de operador: la API real devuelve la intención actualizada (la consola recarga el detalle). */
export const OperatorActionResponseSchema = z.union([
  OperatorActionResultSchema,
  PaymentIntentSchema.transform(() => ({ detail: null, message: 'Acción registrada' })),
]);

export const CustomerListResponseSchema = z.union([
  AdminCustomerPageSchema,
  z
    .object({
      data: z.array(
        z.looseObject({
          id: z.string(),
          fullName: z.string(),
          documentType: AdminCustomerPageSchema.shape.data.element.shape.documentType,
          maskedDocument: z.string(),
          maskedPhone: z.string(),
          businessName: z.string().nullable(),
          status: AdminCustomerPageSchema.shape.data.element.shape.status,
        }),
      ),
      nextCursor: z.string().nullable(),
    })
    .transform((page) => ({
      data: page.data.map((c) => ({
        id: c.id,
        fullName: c.fullName,
        documentType: c.documentType,
        maskedDocument: c.maskedDocument,
        maskedPhone: c.maskedPhone,
        businessName: c.businessName,
        city: null,
        status: c.status,
        lineIds: [],
        activeApplication: null,
      })),
      nextCursor: page.nextCursor,
    })),
]);

export const CustomerDetailResponseSchema = z.union([
  AdminCustomerDetailSchema,
  z
    .object({
      customer: CustomerProfileSchema,
      lines: z.array(CreditLineSummarySchema),
      recentPayments: z.array(PaymentIntentSchema),
    })
    .transform(({ customer: c, lines, recentPayments }) => ({
      id: c.id,
      fullName: c.fullName,
      documentType: c.documentType,
      maskedDocument: c.maskedDocument,
      maskedPhone: c.phone,
      businessName: c.businessName ?? null,
      city: null,
      status: c.status,
      lineIds: lines.map((l) => l.id),
      activeApplication: c.activeApplicationId
        ? { id: c.activeApplicationId, phase: '—', status: 'IN_PROGRESS' }
        : null,
      email: c.email ?? null,
      createdAt: recentPayments.at(-1)?.createdAt ?? new Date(0).toISOString(),
      linkedAccounts: c.linkedAccounts.map((acc) => ({
        id: acc.id,
        participantName: acc.participantName,
        accountType: acc.accountType,
        maskedNumber: acc.maskedNumber,
        isDefault: acc.isDefault,
      })),
      lines,
      applications: [],
      frequentPayees: [],
      recentPayments: recentPayments.map((intent) => intentToSummary(intent)),
    })),
]);

export const LineDetailResponseSchema = z.union([
  AdminLineDetailSchema,
  z
    .object({
      line: CreditLineDetailSchema,
      drawdowns: z.array(DrawdownSchema),
      statements: z.array(StatementSchema),
      movements: z.array(CreditMovementSchema),
    })
    .transform((real) => ({
      line: real.line,
      customer: unknownCustomer(''),
      drawdowns: real.drawdowns.map((d) => ({ ...d, paymentIntentId: null })),
      statements: real.statements,
      payments: [],
      movements: real.movements,
    })),
]);
